// theme.service.ts
import {Injectable} from '@angular/core';
import {FontService} from './font.service';
import {VenueBranding} from '../models/FSConfig';
import {Meta} from '@angular/platform-browser';

interface Theme {
  primary: string;
  contrast: string;
  text: string;
  text2: string
  text3: string;
  surface: string;
  surface2: string;
  surfacex: string;
  notification: string;
  notification2: string;
  warning: string;
  surface80mix: string;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  currentTheme = 'none';
  customFontStyle?: string;
  outlineColor?: string;

  constructor(private fontService: FontService, private meta: Meta ) {
    document.documentElement.style.setProperty('--theme-font-family', 'Outfit');
  }

  async setTheme(branding: VenueBranding) {
    // Default to light theme if theme not found
    if (!this.themes[branding.theme]) {
      branding.theme = 'light';
    }

    const useBoxyLayout = branding.roundness == 'boxy';
    document.documentElement.style.setProperty('--theme-radius', useBoxyLayout ? '0' : 'inherit');

    const theme = this.themes[branding.theme];
    const primary = this.parseColor(branding.primary_color, theme.primary);
    const calculatedSurface = this.hslCalc(primary,8,branding.theme=="light"?89:32);
    const surface2 = this.parseColor(branding.secondary_color, calculatedSurface)
    const contrast = this.parseColor(branding.contrast_color, theme.contrast);
    const bodyColor = this.parseColor(branding.body_color, surface2);
    const notificationColor = this.parseColor(branding.notification_color, theme.notification);
    const notification2Color = this.parseColor(branding.notification2_color, theme.notification2);
    const secondaryButtonColor = this.parseColor(branding.secondary_button_color, primary);
    let dividerColor = this.parseColor(branding.divider_color, primary);

    //Opacity background
    document.documentElement.style.setProperty('--surface-80-mix', theme.surface80mix);

    // Custom theme colors
    document.documentElement.style.setProperty('--theme-primary', primary);
    document.documentElement.style.setProperty('--theme-surface2', surface2);
    document.documentElement.style.setProperty('--theme-contrast', contrast);
    document.documentElement.style.setProperty('--theme-body', bodyColor);
    document.documentElement.style.setProperty('--theme-secondary-button', secondaryButtonColor);

    //If divider not set opacity tp 20%
    if (dividerColor === primary) {
      dividerColor = this.addOpacity(dividerColor, 0.2);
    }
    document.documentElement.style.setProperty('--theme-divider', dividerColor);

    let text;
    let text2;
    let text3;
    let surfacex;
    let notification;
    let surface;
    if (['light', 'dark'].includes(branding.theme)) {
      text = branding.theme === "light" ?  this.hslCalc(primary,3,5) : this.hslCalc(primary,2,98);
      text2 = branding.theme === "light" ?  this.hslCalc(primary,3,25) : this.hslCalc(primary,2,96);
      text3 = branding.theme === "light" ?  this.hslCalc(primary,3,55) : this.hslCalc(primary,3,55);
      const calcSurface = branding.theme === "light" ?  this.hslCalc(primary,2,96) : this.hslCalc(primary,8,5);
      surface = this.parseColor(branding.surface_color, calcSurface);
      surfacex = theme.surfacex;
      notification = notificationColor;
    } else {
      text = theme.primary;
      text2 = theme.primary + "e0";
      text3 = theme.primary + "c0";
      surface = this.parseColor(branding.surface_color, this.hslCalc(primary,2,96));
      surfacex = surface;
      notification = theme.primary;
    }

    if (branding.outline_color) {
      const outlineColor = this.parseColor(branding.outline_color, primary);
      this.outlineColor = outlineColor;
      document.documentElement.style.setProperty('--theme-outline', outlineColor);
    }

    if (branding.text_color) {
      text = this.parseColor(branding.text_color, text);
      text2 = this.addOpacity(text, 0.9);
      text3 = this.addOpacity(text, 0.7);
    }

    document.documentElement.style.setProperty('--theme-text', text);
    document.documentElement.style.setProperty('--theme-text2', text2);
    document.documentElement.style.setProperty('--theme-text3', text3);
    document.documentElement.style.setProperty('--theme-surface', surface);
    document.documentElement.style.setProperty('--theme-surfacex', surfacex);

    // Receipt colors come from the base theme only — see receiptThemes
    const receipt = this.receiptThemes[branding.theme === 'dark' ? 'dark' : 'light'];
    document.documentElement.style.setProperty('--theme-receipt-bg', receipt.bg);
    document.documentElement.style.setProperty('--theme-receipt-text', receipt.text);

    // From theme
    const notificationText = this.pickFirstOkColor(notification, [contrast, text, ]);
    const notification2Text = this.pickFirstOkColor(notification2Color, [contrast, text]);
    document.documentElement.style.setProperty('--theme-notification', notification);
    document.documentElement.style.setProperty('--theme-notification-text', notificationText);
    document.documentElement.style.setProperty('--theme-notification2', notification2Color);
    document.documentElement.style.setProperty('--theme-notification2-text', notification2Text);
    document.documentElement.style.setProperty('--theme-warning', theme.warning);

    // Load and set font
    try {
      await this.fontService.loadFont(branding.font);
      // Font service already sets the CSS variable with proper font family name
    } catch (error) {
      console.error('Failed to load font:', error);
      // Fallback to default font if loading fails
      document.documentElement.style.setProperty('--theme-font-family', 'Outfit, sans-serif');
    }

    // Load custom font
    if (branding.custom_font) {
      try {
        await this.fontService.loadCustomFont(branding.custom_font);
      } catch (error) {
        console.error('Failed to load custom font:', error);
      }
      //this.customFontStyle = "font-size: 2.25rem; line-height: 2.5rem; text-transform:uppercase; letter-spacing: 0.025em; color: rgb(255 0 0 / 0%); -webkit-text-stroke: 1px #ff450a";
      this.customFontStyle = branding.custom_font_style;
    }

    if (branding.theme==="dark") {
      // else 0.3
      document.documentElement.style.setProperty('--theme-overlay-opacity', '0.7');
    }

    this.updateMetaThemeColor(surface);
  }

  /**
   * Updates the meta tag for the theme color to ensure the browser's UI matches the theme
   * @param color - The color to set for the theme.
   */
  private updateMetaThemeColor(color: string) {
    this.meta.removeTag('name="theme-color"');
    this.meta.addTags([
      { name: 'theme-color', content: color },
    ]);
  }

  private parseColor(colorStr: string | undefined, def: string) {
    if (!colorStr) {
      return def;
    }
    if (colorStr.startsWith('#')) {
      return colorStr;
    }
    return `#${colorStr}`;
  }

  // Receipts are locked to two fixed variants — the base theme picks one, branding colors never apply.
  // A receipt has to stay legible no matter how a venue is branded, so it is deliberately not themeable.
  receiptThemes: Record<string, { bg: string, text: string }> = {
    light: { bg: '#FDFDFC', text: '#0D0D0C' },
    dark: { bg: '#201C13', text: '#F9FAFB' },
  };

  // Predefined themes
  themes: Record<string, Theme> = {
    light: {
      primary: '#0D0D0C',
      contrast: '#F5F4EF',
      //background: '#ffffff',
      text: '#0D0D0C',
      text2: '#403F3E',
      text3: '#8C8B88',
      //accent: '#F5F4EF',
      surface: '#FDFDFC',
      surface2: '#E3DCCF',
      surfacex: '#FDFDFC',
      notification: '#1C7ED6',
      notification2: '#E8795D',
      warning: '#E63946',
      surface80mix: "80%",
    },
    dark: {
      primary: '#f9fafb',
      contrast: '#1f2937',
      //background: '#1f2937',
      text: '#f9fafb',
      text2: '#f9fafb',
      text3: '#8C8B88',
      //accent: '#fbbf24',
      surface: '#0D0D0C',
      surface2: '#201C13',
      surfacex: '#201C13',
      notification: '#1C7ED6',
      notification2: '#E8795D',
      warning: "#E63946",
      surface80mix: "80%",
    },
    alt1: {
      primary: '#ff450a',
      contrast: '#1f2937',
      text: '',
      text2: '',
      text3: '',
      surface: '',
      surface2: '',
      surfacex: '',
      notification: '',
      notification2: '#2e0b00',
      warning: "E63946",
      surface80mix: "100%",
    }
  };



  /**
   * Converts a hex color string to an RGB object.
   * @param hex - The hex color string (e.g., "#ff3040" or "ff3040").
   * @returns An object with r, g, and b properties.
   */
  hexToRgb(hex: string): { r: number; g: number; b: number } {
    // Remove any leading '#' character.
    hex = hex.replace(/^#/, '');

    // If the hex string is in shorthand format, convert it to full form.
    if (hex.length === 3) {
      hex = hex.split('').map((char) => char + char).join('');
    }

    // Parse the red, green, and blue components.
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return { r, g, b };
  }

  /**
   * Converts an RGB color to HSL.
   * @param r - Red component (0-255)
   * @param g - Green component (0-255)
   * @param b - Blue component (0-255)
   * @returns An object with h (hue in degrees), s (saturation in percent), and l (lightness in percent).
   */
  rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
    // Normalize red, green, and blue values to the [0, 1] range.
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0,
      s = 0;
    const l = (max + min) / 2;

    // If max and min are the same, the color is achromatic.
    if (max === min) {
      h = 0;
      s = 0;
    } else {
      const delta = max - min;
      s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);

      // Calculate hue based on which color channel is the largest.
      switch (max) {
        case r:
          h = (g - b) / delta + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / delta + 2;
          break;
        case b:
          h = (r - g) / delta + 4;
          break;
      }
      h /= 6;
    }

    // Convert hue to degrees and saturation/lightness to percentages.
    return { h: h * 360, s: s * 100, l: l * 100 };
  }

  /**
   * Converts an HSL color value to a hex color string.
   *
   * @param h - Hue in degrees (0 to 360).
   * @param s - Saturation in percent (0 to 100).
   * @param l - Lightness in percent (0 to 100).
   * @returns The hex color string in the format "#rrggbb".
   */
  hslToHex(h: number, s: number, l: number): string {
    // Convert saturation and lightness to the range [0, 1].
    s /= 100;
    l /= 100;

    // Calculate chroma
    const c = (1 - Math.abs(2 * l - 1)) * s;
    // Calculate a second component
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    // Match lightness
    const m = l - c / 2;

    let rPrime = 0;
    let gPrime = 0;
    let bPrime = 0;

    if (h >= 0 && h < 60) {
      rPrime = c;
      gPrime = x;
      bPrime = 0;
    } else if (h >= 60 && h < 120) {
      rPrime = x;
      gPrime = c;
      bPrime = 0;
    } else if (h >= 120 && h < 180) {
      rPrime = 0;
      gPrime = c;
      bPrime = x;
    } else if (h >= 180 && h < 240) {
      rPrime = 0;
      gPrime = x;
      bPrime = c;
    } else if (h >= 240 && h < 300) {
      rPrime = x;
      gPrime = 0;
      bPrime = c;
    } else if (h >= 300 && h < 360) {
      rPrime = c;
      gPrime = 0;
      bPrime = x;
    }

    // Convert the r', g', b' values to 0-255 range.
    const r = Math.round((rPrime + m) * 255);
    const g = Math.round((gPrime + m) * 255);
    const b = Math.round((bPrime + m) * 255);

    // Convert each channel to a two-digit hex value and return the concatenated string.
    const toHex = (n: number): string => n.toString(16).padStart(2, '0');

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  private hslCalc(color: string, s1: number, l1: number) {
    const { r, g, b } = this.hexToRgb(color);
    const { h, s, l } = this.rgbToHsl(r, g, b);
    const rgb = this.hslToHex(h, s1, l1);
    return rgb;
  }

  color(colorName: string): string {
    return `var(--theme-${colorName})`;
  }

  /*
  Picks the first color from the list that has sufficient contrast against the background.
  */
  private pickFirstOkColor(background: string, colors: string[]) {
    // Loop through the colors and return the first one that has sufficient contrast
    for (const color of colors) {
      const contrastRatio = this.calculateAdjustedContrast(background, color);
      // APCA threshold of 60 is roughly equivalent to WCAG 4.5:1
      if (contrastRatio >= 60) {
        return color;
      }
    }

    // If no color has sufficient contrast, determine if white or black is better
    const whiteContrast = this.calculateAdjustedContrast(background, '#ffffff');
    const blackContrast = this.calculateAdjustedContrast(background, '#000000');

    // If neither has good contrast, prefer the better one
    return (whiteContrast > blackContrast) ? '#ffffff' : '#000000';
  }

  /**
   * Calculates the perceptually adjusted contrast ratio using APCA
   * (Advanced Perceptual Contrast Algorithm).
   *
   * @param background - Background color in hex format
   * @param textColor - Text color in hex format
   * @returns The APCA contrast value (ranges roughly from 0 to 110)
   */
  private calculateAdjustedContrast(background: string, textColor: string): number {
    return Math.abs(this.calculateAPCAContrast(background, textColor));
  }

  /**
   * Calculates contrast using the APCA (Advanced Perceptual Contrast Algorithm).
   * Based on https://github.com/Myndex/SAPC-APCA
   *
   * @param background - Background color in hex format
   * @param text - Text color in hex format
   * @returns APCA contrast value (negative means light text on dark bg)
   */
  private calculateAPCAContrast(background: string, text: string): number {
    // Extract RGB values
    const bgRGB = this.hexToRgb(background);
    const textRGB = this.hexToRgb(text);

    // Convert to Y (luminance) using the APCA method
    const bgY = this.sRGBtoY(bgRGB.r, bgRGB.g, bgRGB.b);
    const textY = this.sRGBtoY(textRGB.r, textRGB.g, textRGB.b);

    // Determine polarity (light text on dark bg or dark text on light bg)
    let contrast: number;
    if (bgY > textY) {
      // Dark text on light background
      contrast = this.calculateAPCAPolarity(bgY, textY);
    } else {
      // Light text on dark background
      contrast = -this.calculateAPCAPolarity(textY, bgY);
    }

    return contrast;
  }

  /**
   * Converts sRGB values to Y luminance for APCA
   */
  private sRGBtoY(r: number, g: number, b: number): number {
    // Convert to linear light (0-1)
    const rLinear = this.linearize(r / 255);
    const gLinear = this.linearize(g / 255);
    const bLinear = this.linearize(b / 255);

    // Apply APCA coefficients - these differ from standard luminance
    return (rLinear * 0.2126729 + gLinear * 0.7151522 + bLinear * 0.0721750);
  }

  /**
   * Converts sRGB to linear light
   */
  private linearize(val: number): number {
    if (val <= 0.04045) {
      return val / 12.92;
    }
    return Math.pow((val + 0.055) / 1.055, 2.4);
  }

  /**
   * Calculates APCA contrast with appropriate polarity
   */
  private calculateAPCAPolarity(Ybg: number, Ytxt: number): number {
    // Soft clamp Y values
    if (Ybg <= 0.022) Ybg = 0.022;
    if (Ytxt <= 0.022) Ytxt = 0.022;

    // The actual APCA algorithm
    let normBG = Math.pow(Ybg, 0.56);
    let normTXT = Math.pow(Ytxt, 0.57);

    let dY = Ybg - Ytxt;

    // Constants and scaling
    const scaleBoW = 1.14;  // dark text on light background
    const loBoWoffset = 0.027;
    const scaleWoB = 1.14;  // light text on dark background

    if (dY >= 0) {
      // For dark text on light background
      if (Ybg >= 0.65) {
        // Scale for high luminance backgrounds
        normBG = normBG * scaleBoW;
      }

      if (Ybg <= 0.02) {
        // Offset for very low luminance backgrounds
        normBG = normBG + loBoWoffset;
      }
    } else {
      // For light text on dark background
      if (Ybg <= 0.35) {
        // Scale for low luminance backgrounds
        normBG = normBG * scaleWoB;
      }
    }

    // Calculate the contrast
    let rawContrast = (normBG - normTXT) * 1.14;

    // Exponents and scaling
    if (rawContrast < 0) {
      // Light text on dark background
      rawContrast = Math.abs(rawContrast) * 1.1;
    }

    // Apply power curve and scale
    if (rawContrast > 0.1) {
      return Math.floor(Math.pow(rawContrast, 1.33) * 100);
    } else {
      return 0; // No measurable contrast
    }
  }

  /**
   * Determines if a color has a dominant blue component
   * @param color - Color in hex format
   * @returns True if the color has a dominant blue component
   */
  private isBlueish(color: string): boolean {
    const { r, g, b } = this.hexToRgb(color);
    // Check if blue is the dominant channel and significantly higher than red
    return b > r + 30 && b > g && b > 100;
  }

  /**
   * Determines if a color is light (close to white)
   * @param color - Color in hex format
   * @returns True if the color is light
   */
  private isLightColor(color: string): boolean {
    const { r, g, b } = this.hexToRgb(color);
    // Simple check: all channels are high
    return r > 220 && g > 220 && b > 220;
  }

  /**
   * Calculates the contrast ratio between two colors according to WCAG guidelines.
   * The ratio ranges from 1:1 (no contrast) to 21:1 (max contrast).
   * @param color1 - First color in hex format (e.g., "#ffffff")
   * @param color2 - Second color in hex format (e.g., "#000000")
   * @returns The contrast ratio between the two colors as a number
   */
  private calculateContrast(color1: string, color2: string): number {
    // Convert colors to RGB
    const rgb1 = this.hexToRgb(color1);
    const rgb2 = this.hexToRgb(color2);

    // Calculate relative luminance for each color
    const luminance1 = this.calculateRelativeLuminance(rgb1.r, rgb1.g, rgb1.b);
    const luminance2 = this.calculateRelativeLuminance(rgb2.r, rgb2.g, rgb2.b);

    // Calculate contrast ratio
    const lighter = Math.max(luminance1, luminance2);
    const darker = Math.min(luminance1, luminance2);

    return (lighter + 0.05) / (darker + 0.05);
  }

  /**
   * Calculates the relative luminance of an RGB color according to WCAG.
   * @param r - Red component (0-255)
   * @param g - Green component (0-255)
   * @param b - Blue component (0-255)
   * @returns Relative luminance value
   */
  private calculateRelativeLuminance(r: number, g: number, b: number): number {
    // Normalize RGB values to the range [0, 1]
    const sR = r / 255;
    const sG = g / 255;
    const sB = b / 255;

    // Calculate the linearized RGB components
    const R = sR <= 0.03928 ? sR / 12.92 : Math.pow((sR + 0.055) / 1.055, 2.4);
    const G = sG <= 0.03928 ? sG / 12.92 : Math.pow((sG + 0.055) / 1.055, 2.4);
    const B = sB <= 0.03928 ? sB / 12.92 : Math.pow((sB + 0.055) / 1.055, 2.4);

    // Calculate luminance using the WCAG formula
    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
  }

  nearestColor(base: string, colors: string[]) {
    let nearest = colors[0];
    let minDistance = Number.MAX_VALUE;

    for (const color of colors) {
      const distance = this.colorDistance(base, color);
      if (distance < minDistance) {
        minDistance = distance;
        nearest = color;
      }
    }

    return nearest;
  }

  private colorDistance(base: string, color: string) {
    const baseRGB = this.hexToRgb(base);
    const colorRGB = this.hexToRgb(color);

    const rDiff = baseRGB.r - colorRGB.r;
    const gDiff = baseRGB.g - colorRGB.g;
    const bDiff = baseRGB.b - colorRGB.b;

    return Math.sqrt(rDiff * rDiff + gDiff * gDiff + bDiff * bDiff);
  }

  private addOpacity(dividerColor: string, number: number) {
    const { r, g, b } = this.hexToRgb(dividerColor);
    const a = Math.round(number * 255);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}${a.toString(16).padStart(2, '0')}`;
  }
}
